import { observable, configure, action } from 'mobx';
import util from 'preact-util';
import LocalModel from './localModel';

configure({ enforceActions: 'always' });

// Officer-panel store for the Megalink range import (spec
// docs/superpowers/specs/2026-10-08-megalink-import-design.md §5 [6]).
// Every method returns the API response; after an await it only writes
// observables through updateKeyValue.
export class MegalinkShotStore extends LocalModel {
    constructor() {
        super('megalinkShot', {
            namePlural: 'megalinkShots',
            namePluralReal: 'megalinkshots',
            sort: 'shotAtServer',
            limit: 2000,
            api: {
                search: { url: '/api/megalinkshots/', params: { limit: 2000, sort: 'shotAtServer' } },
                load: { url: '/api/megalinkshots/', params: {} },
                save: { url: '/api/megalinkshots/', params: {} },
                delete: { url: '/api/megalinkshots/', params: {} },
            },
        });
    }

    @observable megalinkShot = {};

    @observable megalinkShots = [];

    @observable summary = {};

    @observable loadingSummary = false;

    @observable lastError = '';

    // Own shot history for the profile «Skudd» tab (GET /api/megalinkshots/mine).
    @observable history = { count: 0, lags: [] };

    @observable historyDays = 365;

    @observable historyLoading = false;

    // Count alone (?countOnly=1): the profile shows the tab only when > 0.
    @observable historyCount = 0;

    @action
    clearSummary() {
        this.summary = {};
        this.lastError = '';
    }

    async loadMineCount() {
        const response = await util.fetchApi('/api/megalinkshots/mine', { method: 'GET' }, { countOnly: 1 });
        if (response?.status === 200) {
            this.updateKeyValue('historyCount', response.data?.count || 0);
        }
        return response;
    }

    async loadMine({ days = this.historyDays } = {}) {
        this.updateKeyValue('historyDays', days);
        this.updateKeyValue('historyLoading', true);
        const response = await util.fetchApi('/api/megalinkshots/mine', { method: 'GET' }, { days });
        if (response?.status === 200) {
            const data = response.data || {};
            this.updateKeyValue('history', { count: data.count || 0, lags: Array.isArray(data.lags) ? data.lags : [] });
        } else {
            this.updateKeyValue('lastError', response?.message || 'load-failed');
        }
        this.updateKeyValue('historyLoading', false);
        return response;
    }

    async loadSummary(conventionId, stageMd5) {
        this.updateKeyValue('loadingSummary', true);
        const response = await util.fetchApi('/api/megalinkshots/summary', { method: 'GET' }, { convention: conventionId, stage: stageMd5 });
        if (response?.status === 200) {
            this.updateKeyValue('summary', response.data || {});
        } else {
            this.updateKeyValue('lastError', response?.message || 'load-failed');
        }
        this.updateKeyValue('loadingSummary', false);
        return response;
    }

    async loadShots(query = {}) {
        const response = await util.fetchApi('/api/megalinkshots/', { method: 'GET' }, query);
        if (response?.status === 200) {
            this.updateKeyValue('megalinkShots', response.data || []);
        }
        return response;
    }

    async act(shotId, body) {
        const response = await util.fetchApi(`/api/megalinkshots/${shotId}`, { method: 'PATCH' }, body);
        this.updateKeyValue('lastError', response?.status === 202 ? '' : (response?.message || 'action-failed'));
        return response;
    }

    async recompute(conventionId, stageMd5) {
        const response = await util.fetchApi('/api/megalinkshots/recompute', { method: 'POST' }, { convention: conventionId, stage: stageMd5 });
        this.updateKeyValue('lastError', response?.status === 202 ? '' : (response?.message || 'recompute-failed'));
        return response;
    }

    // «Tildel alle etter standplass»: unassigned / stale shots → shooter on their lane in one Lag.
    async assignByLane(conventionId, stageMd5, shotIds) {
        const response = await util.fetchApi('/api/megalinkshots/assign-by-lane', { method: 'POST' }, {
            convention: conventionId, stage: stageMd5, shotIds,
        });
        this.updateKeyValue('lastError', response?.status === 202 ? '' : (response?.message || 'assign-by-lane-failed'));
        return response;
    }

    async revertCell(conventionResultId) {
        const response = await util.fetchApi('/api/megalinkshots/revert-cell', { method: 'POST' }, { conventionResult: conventionResultId });
        this.updateKeyValue('lastError', response?.status === 202 ? '' : (response?.message || 'revert-failed'));
        return response;
    }
}

const store = new MegalinkShotStore();
export default store;
